import {
  cnpjToApiPayload,
  formatCnpjMasked,
  normalizeCnpjAlphanumeric,
} from '@solfacil/frontend-utils/cnpj';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';
import { z } from 'zod';

import useSearchParamsState from '~/hooks/useSearchParamsState';
import { PersonAPI } from '~/services/person-api/person-api.types';

import type { UsePartnersFiltersParams } from '../types';

import partnersFiltersSchema from '../schemas/filtersSchema';

type PartnersFilterForm = z.infer<typeof partnersFiltersSchema>;

const INITIAL_FILTER_STATE: UsePartnersFiltersParams = {
  document: '',
  page: 1,
  page_size: 20,
  partial_name: '',
};

export default function usePartnersFilters(options?: {
  onFiltersChange?: () => void;
}) {
  const searchParams = useSearchParamsState<UsePartnersFiltersParams>(
    INITIAL_FILTER_STATE,
    { parseArrays: false },
  );

  const filterForm = useForm<PartnersFilterForm>({
    initialValues: {
      document: searchParams.value.document
        ? formatCnpjMasked(normalizeCnpjAlphanumeric(searchParams.value.document))
        : '',
      partial_name: searchParams.value.partial_name || '',
    },
    validationSchema: toTypedSchema(partnersFiltersSchema),
  });

  const filterParams = computed<Partial<PersonAPI.ListPartnersParams>>(() => {
    const params = searchParams.value;
    const filteredParams: Partial<PersonAPI.ListPartnersParams> = {};

    if (params.document?.trim()) {
      filteredParams.document = params.document.trim();
    }

    if (params.partial_name?.trim()) {
      filteredParams.partial_name = params.partial_name.trim();
    }

    filteredParams.page = Number(params.page) || 1;
    filteredParams.page_size = Number(params.page_size) || 20;

    return filteredParams;
  });

  function applyFiltersAutomatically(values: any) {
    const filteredValues: Partial<UsePartnersFiltersParams> = {
      page: 1,
      page_size: Number(searchParams.value.page_size) || 20,
    };

    if (values.document?.trim()) {
      filteredValues.document = cnpjToApiPayload(values.document);
    } else {
      filteredValues.document = '';
    }

    if (values.partial_name?.trim()) {
      filteredValues.partial_name = values.partial_name.trim();
    } else {
      filteredValues.partial_name = '';
    }

    searchParams.value = filteredValues as UsePartnersFiltersParams;

    if (options?.onFiltersChange) {
      options.onFiltersChange();
    }
  }

  watch(() => filterForm.values.document, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (!newValue || (newValue && normalizeCnpjAlphanumeric(newValue).length === 14)) {
        applyFiltersAutomatically(filterForm.values);
      }
    }
  });

  watch(() => filterForm.values.partial_name, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      applyFiltersAutomatically(filterForm.values);
    }
  });

  function handleSearch() {
    return filterForm.handleSubmit((values) => {
      applyFiltersAutomatically(values);
    })();
  }

  const handleReset = () => {
    searchParams.value = {
      document: '',
      page: 1,
      page_size: 20,
      partial_name: '',
    } as UsePartnersFiltersParams;

    filterForm.resetForm({
      values: {
        document: '',
        partial_name: '',
      },
    });
  };

  const initializeForm = () => {
    filterForm.setValues({
      document: searchParams.value.document
        ? formatCnpjMasked(normalizeCnpjAlphanumeric(searchParams.value.document))
        : '',
      partial_name: searchParams.value.partial_name || '',
    });
  };

  function setPage(value: number) {
    const currentParams = { ...searchParams.value };
    currentParams.page = Number(value);
    searchParams.value = currentParams;
  }

  function setPageSize(value: number) {
    const currentParams = { ...searchParams.value };
    currentParams.page_size = Number(value);
    currentParams.page = 1;
    searchParams.value = currentParams;
  }

  return {
    filterForm,
    filterParams,
    handleReset,
    handleSearch,
    initializeForm,
    searchParams,
    setPage,
    setPageSize,
  };
}
